import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import type { LessonDrillRef } from '../types/lesson';
import VideoFrameThumb from './VideoFrameThumb';
import { Play, Tag } from '@phosphor-icons/react';

interface AttachedDrillsProps {
  drills: LessonDrillRef[];
  /** Section title override */
  title?: string;
}

export function AttachedDrills({ drills, title = 'Practice Drills' }: AttachedDrillsProps) {
  // One drill at a time — holding the drill itself (not an id) makes that
  // structural rather than a rule to remember.
  const [videoDrill, setVideoDrill] = useState<LessonDrillRef | null>(null);
  const [infoModalDrill, setInfoModalDrill] = useState<LessonDrillRef | null>(null);
  const loading = false;

  // Escape to close, and lock body scroll while either modal is open. Same
  // approach as PlayerProfileModal / ProgressReportModal; the info modal in
  // this file never had it.
  const anyModalOpen = !!videoDrill || !!infoModalDrill;
  useEffect(() => {
    if (!anyModalOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setVideoDrill(null);
      setInfoModalDrill(null);
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [anyModalOpen]);

  if (drills.length === 0) return null;

  return (
    <div data-testid="attached-drills">
      {/* Section heading */}
      <div className="flex items-center justify-between" style={{ marginBottom: 16 }}>
        {/* Section 8.2, matching every other header on the pane. */}
        <div
          className="text-[13px] leading-[18px] font-semibold uppercase text-[rgba(60,60,67,0.6)]"
          style={{ letterSpacing: '0.5px' }}
        >
          {title}
        </div>
        {/* Same size and colour, weight 400: a count beside a header, not a
            second half of it. */}
        {!loading && drills.length > 0 && (
          <div className="text-[13px] leading-[18px] font-normal text-[rgba(60,60,67,0.6)]">
            {drills.length} drill{drills.length !== 1 ? 's' : ''}
          </div>
        )}
      </div>

      {/* Drill cards — horizontal scroll on mobile, 2-col grid on desktop */}
      {loading ? (
        <div className="flex gap-3 overflow-x-auto md:grid md:grid-cols-2 md:overflow-visible pb-1">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="bg-white rounded-xl p-2 shadow-sm border border-db-border w-[160px] flex-shrink-0 md:w-auto"
            >
              <div className="aspect-video rounded-lg bg-db-bg animate-pulse" />
              <div className="h-3 bg-db-bg animate-pulse rounded mt-2 w-3/4" />
              <div className="h-3 bg-db-bg animate-pulse rounded mt-1.5 w-1/3" />
            </div>
          ))}
        </div>
      ) : (
        <div className="flex gap-3 overflow-x-auto md:grid md:grid-cols-2 md:overflow-visible pb-1 -mx-1 px-1">
          {drills.map((drill) => {
            return (
              <button
                key={drill.drillId}
                type="button"
                data-testid="attached-drill-card"
                onClick={() => setVideoDrill(drill)}
                className="bg-white rounded-xl p-2 shadow-sm border border-db-border text-left w-[160px] flex-shrink-0 md:w-auto btn-press transition-colors"
              >
                <div className="relative aspect-video rounded-lg overflow-hidden bg-db-bg flex items-center justify-center">
                  {drill.thumbnailUrl ? (
                    <img
                      src={drill.thumbnailUrl}
                      alt={drill.title}
                      className="object-cover w-full h-full"
                    />
                  ) : (
                    <VideoFrameThumb
                      videoUrl={drill.videoUrl || null}
                      alt={drill.title}
                      className="object-cover w-full h-full"
                      /* The same tinted square My Cloud uses: a grey glyph on
                         a grey field read as a broken image rather than as a
                         video with no poster frame. */
                      placeholder={
                        <div
                          className="flex items-center justify-center"
                          style={{ width: 44, height: 44, borderRadius: 8, background: 'rgba(26,107,138,0.12)' }}
                        >
                          <Play size={18} weight="fill" color="#1A6B8A" aria-hidden="true" />
                        </div>
                      }
                    />
                  )}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setInfoModalDrill(drill);
                    }}
                    className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-white/80 backdrop-blur flex items-center justify-center text-db-text-2 hover:text-db-text-1"
                    aria-label={`Info about ${drill.title}`}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="16" x2="12" y2="12" />
                      <line x1="12" y1="8" x2="12.01" y2="8" />
                    </svg>
                  </button>
                </div>
                <div className="text-[13px] font-semibold text-db-text-1 mt-1.5 truncate">
                  {drill.title}
                </div>
                {/* CL-1's icon-plus-text, the shape the coaches grid uses. The
                    tinted pill read as a status badge for what is a category. */}
                <div className="mt-1 flex items-center gap-1 text-[13px] leading-[18px] text-[#1C1C1C] capitalize">
                  <Tag size={14} color="#54C3EA" weight="bold" aria-hidden="true" />
                  {drill.shotType}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Drill video modal — replaces the old inline expansion, so opening a
          drill no longer reflows anything below the grid. */}
      <AnimatePresence>
        {videoDrill && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={() => setVideoDrill(null)}
            className="fixed inset-0 z-50 flex items-center justify-center"
            style={{ background: 'rgba(9,21,32,0.75)' }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.97 }}
              transition={{ duration: 0.15 }}
              onClick={(e) => e.stopPropagation()}
              className="relative bg-white overflow-hidden"
              style={{ maxWidth: 900, width: 'calc(100% - 48px)', borderRadius: 14 }}
            >
              <video
                src={videoDrill.videoUrl}
                controls
                autoPlay
                playsInline
                className="w-full bg-black block"
                style={{ aspectRatio: '16 / 9' }}
              />

              <button
                type="button"
                onClick={() => setVideoDrill(null)}
                aria-label="Close"
                className="absolute flex items-center justify-center rounded-full text-white transition-colors"
                style={{ top: 12, right: 12, width: 32, height: 32, background: 'rgba(9,21,32,0.55)' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(9,21,32,0.75)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(9,21,32,0.55)'; }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>

              <div style={{ padding: '16px 20px' }}>
                <h2 className="text-[16px] font-semibold text-[#1C1C1C]">{videoDrill.title}</h2>
                <div className="mt-1">
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-db-navy-light text-db-navy font-medium capitalize">
                    {videoDrill.shotType}
                  </span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Info modal */}
      <AnimatePresence>
        {infoModalDrill && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setInfoModalDrill(null)}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-xl p-5 max-w-md w-full"
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <h2 className="text-base font-bold text-db-text-1">{infoModalDrill.title}</h2>
                <button
                  type="button"
                  onClick={() => setInfoModalDrill(null)}
                  className="w-7 h-7 rounded-full flex items-center justify-center text-db-text-3 hover:text-db-text-1 hover:bg-db-bg shrink-0"
                  aria-label="Close"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
              <div className="inline-block px-2 py-0.5 rounded-full bg-db-navy-light text-db-navy text-[10px] font-medium uppercase tracking-wider mb-3">
                {infoModalDrill.shotType}
              </div>
              <p className="text-sm text-db-text-1 leading-relaxed whitespace-pre-line mb-5">
                {infoModalDrill.description}
              </p>
              <button
                type="button"
                onClick={() => setInfoModalDrill(null)}
                className="w-full bg-db-navy text-white text-sm font-semibold rounded-lg py-2 btn-press"
              >
                Got it
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
